import React, { useEffect, useState } from "react";
import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  Switch,
  TextField,
  IconButton,
  CircularProgress,
  Typography,
  Divider,
  Paper
} from "@mui/material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import ROUTES from "../../../utils/routes";
import { ROLES } from "../../../utils/config";
import { useDispatch, useSelector } from "react-redux";
import { getDivisions } from "../../../redux/slice/divisionsSlice";
import { getUsersList } from '../../../redux/slice/usersListSlice';
import API from "../../../redux/api";
import { CommonSelect } from "../../../Components/common";
import { CommonButton } from "../../../Components/common/commonButton";
import instance from "../../../redux/api";
import { commonToast } from "../../../Components/common";
import '../../../Components/common/commonPageStyles.css';

const addUserCancel = {
  backgroundColor: "transparent",
  color: "#1672DE",
  border: "1px solid #1672DE",
  borderRadius: "10px",
  padding: "10px 24px",
  fontWeight: "600",
  fontSize: "14px",
  textTransform: "none",
  minHeight: "40px",
  minWidth: "120px",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    backgroundColor: "rgba(22, 114, 222, 0.05)",
    borderColor: "#1565c0",
    color: "#1565c0",
  },
};

const addUserSave = {
  backgroundColor: "#1672DE",
  color: "white",
  borderRadius: "10px",
  padding: "10px 24px",
  fontWeight: "600",
  fontSize: "14px",
  textTransform: "none",
  minHeight: "40px",
  minWidth: "120px",
  boxShadow: "0 4px 16px rgba(22, 114, 222, 0.3)",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "#1565c0",
    boxShadow: "0 6px 24px rgba(22, 114, 222, 0.4)",
    transform: "translateY(-1px)",
  },
  "&:disabled": {
    backgroundColor: "#e0e0e0",
    color: "#9e9e9e",
    boxShadow: "none",
    transform: "none",
  },
};
export default function ManageUser({ type }) {
  // Selectors
  const { usersList } = useSelector((state) => state?.usersList);
  const { divisions } = useSelector((state) => state?.divisions);
  const { manageUser } = useSelector((state) => state?.manageUser);

  // Hooks
  const navigate = useNavigate();

  // Local State
  const [managers, setManagers] = useState([]);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [isProgressBar, setIsProgressBar] = useState(false);
  const [formData, setFormData] = useState({
    userName: "",
    department: "",
    email: "",
    reportingManager: "",
    organisationalRole: "",
    division: "",
    radioButton: true,
    superAdmin: false,
    supervisor: false,
    creditController: false,
    agent: false,
    status: "Active",
  });
  const [updateData, setUpdateData] = useState({});

  const title = { view: "View User", add: "Add User", edit: "Edit User" }[type];

  const handleDecryptClick = async (id, type) => {
    try {
      const response = await instance.post(`/users/user-data-decryption/`, { id, type });
      if (response.status === 200) {
        const decryptedValue = response.data;
        return decryptedValue.decrypted_email;
      }
    } catch (error) {
      console.error('Failed to decrypt:', error);
    }
  };

  const dispatch = useDispatch();

  useEffect(() => {
    if (usersList?.length <= 0) {
      dispatch(getUsersList());
    }
    dispatch(getDivisions());

    if (type === "edit") {
      handleDecryptClick(manageUser.id, 'decrypted_email').then((user_decrypted_email) => {
        setFormData({
          userName: manageUser?.user_name,
          department: manageUser?.department,
          email: user_decrypted_email,
          reportingManager: manageUser?.reporting_manager?.user_name,
          organisationalRole: manageUser?.role,
          division: manageUser?.division?.entity_divisions,
          status: manageUser?.status,
          superAdmin: false,
          supervisor: false,
          creditController: false,
          agent: false,
        });
      });
    }

    if (type !== "add")
      setFormData({
        userName: manageUser?.user_name,
        department: manageUser?.department,
        email: manageUser?.decrypted_email ? manageUser?.decrypted_email : manageUser?.email,
        reportingManager: manageUser?.reporting_manager?.user_name,
        organisationalRole: manageUser?.role,
        division: manageUser?.division?.entity_divisions,
        status: manageUser?.status,
        superAdmin: false,
        supervisor: false,
        creditController: false,
        agent: false,
      });
  }, []);

  useEffect(() => {
    if (usersList?.length > 0) {
      setManagers(
        usersList?.filter(
          (user) => user?.role === "Manager" && user?.status === "Active"
        )
      );
    }
  }, [usersList]);

  const requiredFields = [
    "userName",
    "department",
    "email",
    "organisationalRole",
    "division",
  ];
  const [errors, setErrors] = useState({
    email: "",
  });

  useEffect(() => {
    setIsSaveDisabled(
      !(requiredFields.every((field) => !!formData[field]) && !errors?.email)
    );
  }, [formData, errors]);

  const handleEmailBlur = () => {
    if (type === "add" || type === "edit") validateEmail();
  };

  const handleChange = async (event) => {
    let { name, value } = event.target;
    if (name === "email") value = value.trim();
    if (name === "userName") {
      setUpdateData({ ...updateData, ['user_name']: value });
    } else {
      setUpdateData({ ...updateData, [name]: value });
    }
    setFormData({ ...formData, [name]: value });

    if (name === "email" && value.includes(".com")) validateEmail(value);
  };

  const handleDropdownChange = (name, value, reason, event) => {
    if (reason === "clear") {
      setFormData({ ...formData, [name]: "" });
      setUpdateData({ ...updateData, [name]: "" });
    } else {
      setFormData({ ...formData, [name]: value });
      if (name === "organisationalRole") {
        setUpdateData({ ...updateData, ['role']: value });
      } else if (name === "reportingManager") {
        setUpdateData({ ...updateData, ['reporting_manager']: managers?.filter(
          (manager) => manager?.user_name === value
        )[0] });
      } else if (name === "division") {
        setUpdateData({ ...updateData, ['division']: divisions?.filter(
          (division) => division?.entity_divisions === value
        )[0] });
      } else {
        setUpdateData({ ...updateData, [name]: value });
      }
    }
  };

  const handleSwitchChange = () => {
    setFormData({
      ...formData,
      status: formData.status === "Active" ? "Inactive" : "Active",
    });
    setUpdateData({
      ...updateData,
      status: updateData.status === "Active" ? "Inactive" : "Active",
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const reportingManagerInfo = managers?.filter(
      (item) => item?.user_name === formData.reportingManager
    );
    const reportingManagerId = reportingManagerInfo[0]?.id;

    try {
      const addData = {
        user_name: formData.userName,
        email: formData.email,
        role: formData.organisationalRole,
        status: formData.status,
        department: formData.department,
        reporting_manager:
          type === "add"
            ? reportingManagerId
            : managers?.filter(
                (manager) => manager?.user_name === formData.reportingManager
              )[0],
        division: divisions?.filter(
          (division) => division?.entity_divisions === (formData?.division?.entity_divisions ?? formData?.division)
          )[0]?.id,
      };

      const response =
        type === "add"
          ? await API.post(`users/users_api/user/`, addData)
          : await API.patch(`users/users_api/user/${manageUser?.id}/`, {
              ...updateData,
            });
      if (response.status === 200) {
        commonToast("success", type === "add" ? "User added successfully" : "Successfully updated");
        setErrors({ ...errors, email: "" });
        navigate(ROUTES.AUM);
      } else {
        commonToast("error", "Failed to add user");
      }
    } catch (error) {
      commonToast("error", "Failed to add user");
    }
  };

  const validateEmail = async (currentEmail = null) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    let currentFormEmail = currentEmail ?? formData?.email;
    const isValid = emailRegex.test(currentFormEmail);
    if (isValid) {
      // // check if email is already exists for both add and edit user
      // let emails = [];
      // if (type === "add") {
      //   emails = usersList?.map((user) => user?.email?.toLowerCase());
      // } else if (type === "edit") {
      //   emails = usersList?.map((user) => {
      //     if (user.id !== manageUser?.id) {
      //       return user?.email?.toLowerCase();
      //     }
      //     return null;
      //   });
      //   emails = emails.filter((item) => item != null);
      // }
      // emails?.includes(currentFormEmail.toLowerCase())
      //   ? setErrors({ ...errors, email: "Email already available" })
      //   : setErrors({ ...errors, email: "" });

      if (currentEmail) {
        setIsProgressBar(true);
        const response = await instance.post(`/users/check_user_exists_or_not/`, { currentEmail });
        if (response.status === 200 && response?.data?.message == "Yes") {
          setErrors({ ...errors, email: "Email already exists" });
        } else if (response.status === 200 && response?.data?.message == "No") {
          setErrors({ ...errors, email: "" })
        } else {
          setErrors({ ...errors, email: "Invalid email format" });
        }
        setIsProgressBar(false);
      }
    } else {
      setErrors({ ...errors, email: "Invalid email format" });
    }
  };

  const formLabelStyle = {
    paddingBottom: "8px",
    fontWeight: "600",
    color: "#333",
    fontSize: "14px",
    marginBottom: "4px",
  };

  const ManageCloseIcon = {
    color: "#666",
    transition: "all 0.2s ease",
    "&:hover": {
      color: "#1672DE",
      backgroundColor: "rgba(22, 114, 222, 0.1)",
    },
  };

  useEffect(() => {
    if (updateData) {
      setFormData((prev) => ({
        ...prev,
        ...updateData, //merge all updateData fields into formData
      }));
    }
  }, [updateData]);

  return (
    <>
      <style>
        {`
          * {
            overflow-x: hidden !important;
            overflow-y: hidden !important;
          }
          body, html {
            overflow-x: hidden !important;
            overflow-y: hidden !important;
            max-width: 100vw !important;
            max-height: 100vh !important;
          }
          [class*="MuiBox"],
          [class*="MuiBox"] *,
          [class*="MuiPaper"],
          [class*="MuiPaper"] *,
          [class*="MuiGrid"],
          [class*="MuiGrid"] *,
          [class*="MuiFormControl"],
          [class*="MuiFormControl"] *,
          form,
          form *,
          div,
          div * {
            overflow-x: hidden !important;
            overflow-y: hidden !important;
            scrollbar-width: none !important;
            -ms-overflow-style: none !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }
          [class*="MuiBox"]::-webkit-scrollbar,
          [class*="MuiBox"] *::-webkit-scrollbar,
          [class*="MuiPaper"]::-webkit-scrollbar,
          [class*="MuiPaper"] *::-webkit-scrollbar,
          [class*="MuiGrid"]::-webkit-scrollbar,
          [class*="MuiGrid"] *::-webkit-scrollbar,
          [class*="MuiFormControl"]::-webkit-scrollbar,
          [class*="MuiFormControl"] *::-webkit-scrollbar,
          form::-webkit-scrollbar,
          form *::-webkit-scrollbar,
          div::-webkit-scrollbar,
          div *::-webkit-scrollbar {
            display: none !important;
            width: 0 !important;
            height: 0 !important;
            background: transparent !important;
          }
        `}
      </style>
      <Box
        sx={{
          width: "100%",
          maxWidth: "100vw",
          height: "calc(100vh - 180px)",
          maxHeight: "calc(100vh - 180px)",
          display: "flex",
          flexDirection: "column",
          padding: "20px",
          backgroundColor: "#fafafa",
          overflow: "hidden !important",
          overflowX: "hidden !important",
          overflowY: "hidden !important",
          boxSizing: "border-box",
        }}
      >
        {/* Header */}
        <Box className="page-header-container" sx={{ marginBottom: "20px" }}>
          <Typography variant="h5" className="page-heading">
            {title}
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={() => navigate(ROUTES.AUM)}
            aria-label="close"
            sx={ManageCloseIcon}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Form Container */}
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            backgroundColor: "#ffffff",
            borderRadius: "12px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            border: "1px solid #e8eaf0",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden !important",
            overflowX: "hidden !important",
            overflowY: "hidden !important",
            maxHeight: "100%",
            minHeight: 0,
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
          }}
        >
          <Box 
            sx={{ 
              padding: "24px",
              overflow: "hidden !important",
              overflowX: "hidden !important",
              overflowY: "hidden !important",
              maxHeight: "100%",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: "18px",
                fontWeight: "600",
                color: "#1672DE",
                marginBottom: "16px",
              }}
            >
              Basic Information
            </Typography>
            <Divider sx={{ marginBottom: "24px", borderColor: "#e0e0e0" }} />

            <form 
              onSubmit={handleSubmit} 
              style={{ 
                display: "flex", 
                flexDirection: "column", 
                flex: 1, 
                overflow: "hidden",
                overflowX: "hidden",
                overflowY: "hidden",
                minHeight: 0,
                maxHeight: "100%",
                width: "100%",
                maxWidth: "100%",
                boxSizing: "border-box",
              }}
            >
              <Box 
                sx={{ 
                  flex: 1, 
                  overflow: "hidden !important", 
                  overflowX: "hidden !important",
                  overflowY: "hidden !important",
                  minHeight: 0,
                  maxHeight: "100%",
                  width: "100%",
                  maxWidth: "100%",
                  boxSizing: "border-box",
                }}
              >
                <Grid 
                  container 
                  spacing={3} 
                  sx={{ 
                    overflow: "hidden !important", 
                    overflowX: "hidden !important",
                    overflowY: "hidden !important",
                    maxWidth: "100%",
                    width: "100%",
                    margin: 0,
                    boxSizing: "border-box",
                    "& .MuiGrid-item": {
                      paddingTop: "16px !important",
                    },
                    "& .MuiTextField-root": {
                      "& .MuiOutlinedInput-root": {
                        minHeight: "40px !important",
                        height: "40px !important",
                        "& .MuiOutlinedInput-input": {
                          padding: "9.5px 14px !important",
                          fontSize: "14px !important",
                          height: "100% !important",
                        },
                      },
                    },
                    "& .MuiAutocomplete-root": {
                      "& .MuiOutlinedInput-root": {
                        minHeight: "40px !important",
                        height: "40px !important",
                        "& .MuiOutlinedInput-input": {
                          padding: "9.5px 14px !important",
                          fontSize: "14px !important",
                          height: "100% !important",
                        },
                      },
                    },
                  }}
                >
                  <Grid item xs={12} md={4}>
                    <InputLabel htmlFor="userName" sx={formLabelStyle}>
                      User Name
                    </InputLabel>
                    <TextField
                      fullWidth
                      placeholder="Enter the User Name"
                      value={formData.userName}
                      onChange={handleChange}
                      name="userName"
                      size="small"
                      disabled={type === "view"}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          minHeight: "40px !important",
                          height: "40px !important",
                          "& .MuiOutlinedInput-input": {
                            padding: "9.5px 14px !important",
                            fontSize: "14px !important",
                            height: "100% !important",
                          },
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <InputLabel sx={formLabelStyle}>Status</InputLabel>
                    <TextField
                      fullWidth
                      value=""
                      size="small"
                      disabled
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          minHeight: "40px !important",
                          height: "40px !important",
                          backgroundColor: "transparent",
                          padding: "0 !important",
                          "& fieldset": {
                            border: "none",
                          },
                          "& .MuiOutlinedInput-input": {
                            padding: "0 !important",
                            height: "100% !important",
                            display: "flex",
                            alignItems: "center",
                          },
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              width: "100%",
                              height: "100%",
                              padding: "9.5px 14px",
                              boxSizing: "border-box",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: formData.status === "Active" ? "#1672DE" : "#666",
                                fontWeight: "500",
                                minWidth: "60px",
                                margin: 0,
                                padding: 0,
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              {formData.status || "Active"}
                            </Typography>
                            <Switch
                              checked={formData.status === "Active"}
                              onChange={handleSwitchChange}
                              name="radioButton"
                              disabled={type === "view"}
                              sx={{
                                padding: 0,
                                margin: 0,
                                "& .MuiSwitch-switchBase": {
                                  padding: "2px",
                                  "&.Mui-checked": {
                                    transform: "translateX(20px)",
                                    color: "#fff",
                                    "& + .MuiSwitch-track": {
                                      backgroundColor: "#1672DE",
                                      opacity: 1,
                                    },
                                  },
                                  "&.Mui-disabled": {
                                    color: "#fff",
                                    "& + .MuiSwitch-track": {
                                      backgroundColor: formData.status === "Active" ? "#1672DE" : "#9e9e9e",
                                      opacity: 1,
                                    },
                                  },
                                },
                                "& .MuiSwitch-thumb": {
                                  width: "16px",
                                  height: "16px",
                                  boxShadow: "none",
                                },
                                "& .MuiSwitch-track": {
                                  borderRadius: "12px",
                                  backgroundColor: formData.status === "Active" ? "#1672DE" : "#9e9e9e",
                                  opacity: 1,
                                  height: "20px",
                                  width: "40px",
                                },
                              }}
                            />
                          </Box>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <InputLabel htmlFor="department" sx={formLabelStyle}>
                      Department
                    </InputLabel>
                    <TextField
                      fullWidth
                      placeholder="Enter the Department"
                      value={formData.department}
                      onChange={handleChange}
                      name="department"
                      size="small"
                      disabled={type === "view"}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          minHeight: "40px !important",
                          height: "40px !important",
                          "& .MuiOutlinedInput-input": {
                            padding: "9.5px 14px !important",
                            fontSize: "14px !important",
                            height: "100% !important",
                          },
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box sx={{ position: "relative" }}>
                      <InputLabel htmlFor="email" sx={formLabelStyle}>
                        E-Mail
                      </InputLabel>
                      <TextField
                        fullWidth
                        placeholder="Enter the email"
                        value={formData.email}
                        onChange={handleChange}
                        onBlur={handleEmailBlur}
                        name="email"
                        size="small"
                        error={!!errors.email}
                        helperText={errors.email}
                        disabled={type === "view"}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            minHeight: "40px !important",
                            height: "40px !important",
                            "& .MuiOutlinedInput-input": {
                              padding: "9.5px 14px !important",
                              fontSize: "14px !important",
                              height: "100% !important",
                            },
                          },
                          "& .MuiFormHelperText-root": {
                            fontSize: "12px",
                            marginLeft: 0,
                            marginTop: "4px",
                          },
                        }}
                      />
                      {isProgressBar && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: errors.email ? "35%" : "50%",
                            right: "12px",
                            transform: "translateY(-50%)",
                          }}
                        >
                          <CircularProgress size={20} sx={{ color: "#1672DE" }} />
                        </Box>
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <InputLabel htmlFor="reportingManager" sx={formLabelStyle}>
                      Reporting Manager
                    </InputLabel>
                    <FormControl fullWidth>
                      <CommonSelect
                        name="reportingManager"
                        placeholder="Choose a reporting manager"
                        value={formData.reportingManager}
                        handleChange={(event, value, reason) =>
                          handleDropdownChange(
                            "reportingManager",
                            value,
                            reason,
                            event
                          )
                        }
                        options={managers?.map((option) => option?.user_name)}
                        disabled={type === "view"}
                        customStyles={{
                          "& .MuiOutlinedInput-root": {
                            minHeight: "40px !important",
                            height: "40px !important",
                            "& .MuiOutlinedInput-input": {
                              padding: "9.5px 14px !important",
                              fontSize: "14px !important",
                              height: "100% !important",
                            },
                          },
                        }}
                      />
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <InputLabel htmlFor="organisationalRole" sx={formLabelStyle}>
                      Organisational Role
                    </InputLabel>
                    <FormControl fullWidth>
                      <CommonSelect
                        name="organisationalRole"
                        placeholder="Choose an organisational role"
                        value={formData.organisationalRole}
                        handleChange={(event, value, reason) =>
                          handleDropdownChange(
                            "organisationalRole",
                            value,
                            reason,
                            event
                          )
                        }
                        options={ROLES}
                        disabled={type === "view"}
                        customStyles={{
                          "& .MuiOutlinedInput-root": {
                            minHeight: "40px !important",
                            height: "40px !important",
                            "& .MuiOutlinedInput-input": {
                              padding: "9.5px 14px !important",
                              fontSize: "14px !important",
                              height: "100% !important",
                            },
                          },
                        }}
                      />
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <InputLabel htmlFor="division" sx={formLabelStyle}>
                      Division
                    </InputLabel>
                    <FormControl fullWidth>
                      <CommonSelect
                        name="division"
                        placeholder="Choose a division"
                        value={typeof formData?.division === "object" ? formData?.division?.entity_divisions : formData?.division}
                        handleChange={(event, value, reason) =>
                          handleDropdownChange("division", value, reason, event)
                        }
                        options={divisions?.map((option) => option?.entity_divisions)}
                        disabled={type === "view"}
                        customStyles={{
                          "& .MuiOutlinedInput-root": {
                            minHeight: "40px !important",
                            height: "40px !important",
                            "& .MuiOutlinedInput-input": {
                              padding: "9.5px 14px !important",
                              fontSize: "14px !important",
                              height: "100% !important",
                            },
                          },
                        }}
                      />
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>

              {/* Buttons */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  marginTop: "32px",
                  paddingTop: "24px",
                  borderTop: "1px solid #e0e0e0",
                  flexShrink: 0,
                }}
              >
                <CommonButton
                  text="Cancel"
                  variant="outlined"
                  handleClick={() => {
                    setErrors({ ...errors, email: "" });
                    navigate(ROUTES.AUM);
                  }}
                  sx={{
                    backgroundColor: "transparent !important",
                    color: "#1672DE !important",
                    border: "1px solid #1672DE !important",
                    borderRadius: "10px",
                    padding: "10px 24px",
                    fontWeight: "600",
                    fontSize: "14px",
                    textTransform: "none",
                    minHeight: "40px",
                    minWidth: "120px",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      backgroundColor: "rgba(22, 114, 222, 0.05) !important",
                      borderColor: "#1565c0 !important",
                      color: "#1565c0 !important",
                    },
                  }}
                />
                {type !== "view" && (
                  <CommonButton
                    text="Save"
                    type="submit"
                    variant="contained"
                    disabled={isSaveDisabled || isProgressBar}
                    sx={{
                      backgroundColor: "#1672DE !important",
                      color: "white !important",
                      borderRadius: "10px",
                      padding: "10px 24px",
                      fontWeight: "600",
                      fontSize: "14px",
                      textTransform: "none",
                      minHeight: "40px",
                      minWidth: "120px",
                      boxShadow: "0 4px 16px rgba(22, 114, 222, 0.3)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: "#1565c0 !important",
                        boxShadow: "0 6px 24px rgba(22, 114, 222, 0.4)",
                        transform: "translateY(-1px)",
                      },
                      "&:disabled": {
                        backgroundColor: "#e0e0e0 !important",
                        color: "#9e9e9e !important",
                        boxShadow: "none",
                        transform: "none",
                      },
                    }}
                    hoverColor="#1565c0"
                  />
                )}
              </Box>
            </form>
          </Box>
        </Paper>
      </Box>
    </>
  );
}
